/**
 * API Service for game backend communication
 */

// Dynamic API URL based on current hostname
const getApiBase = () => {
  const { protocol, hostname } = window.location;

  // Production: ar-mandarin.app -> api.ar-mandarin.app
  if (hostname === 'ar-mandarin.app' || hostname === 'www.ar-mandarin.app') {
    return `${protocol}//api.ar-mandarin.app`;
  }

  // Development: localhost with port 8000
  return `${protocol}//${hostname}:8000`;
};

const API_BASE = getApiBase();

export interface LoginResponse {
  success: boolean;
  player_id: string | null;
  error: string | null;
}

export interface GameState {
  status: 'waiting' | 'playing';
  game_uuid: string;
  players: PlayerInfo[];
  player_count: number;
  online_count: number;  // All connected users (including spectators)
  max_players: number;
  min_players_to_start: number;
  can_start: boolean;
  is_joinable: boolean;
  is_playing: boolean;
  // Backend-computed fields
  game_stats?: {
    finished_count: number;
    playing_count: number;
    waiting_count: number;
  };
  still_racing_players?: Array<{
    player_id: string;
    current_level: number;
    progress: number;
  }>;
  players_needed_to_start?: number;
  // Game in progress
  game_time_remaining?: number;
  winner_player_id?: string | null;
  rankings?: string[];  // Ordered list of player_ids by finish order
  // Level details for requesting player (only during playing state)
  current_level?: LevelInfo;
}

export interface PlayerInfo {
  player_id: string;
  status: 'waiting' | 'playing' | 'finished';
  seat_index: number;
  connected: boolean;
  current_level: number;
  progress: number;
  finished_at?: string | null;
  finish_rank?: number | null;
}

export interface LevelInfo {
  level_id: number;
  name_en: string;
  name_zh: string;
  grammar_pattern: string;
  task_description: string;
  task_image?: string;  // Optional image filename for the task
  time_limit_seconds: number;
  expected_answers: string[][];
}

export interface ActionResponse {
  success: boolean;
  error?: string;
  seat_index?: number;
  game_uuid?: string;
  is_correct?: boolean;
  level_completed?: boolean;
  game_completed?: boolean;
  new_level?: number;
  answers_remaining?: number;
}

/**
 * Login/validate player ID
 */
const headers = {
  'Content-Type': 'application/json',
};

export async function login(playerId: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE}/api/login`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ player_id: playerId }),
  });
  return response.json();
}

/**
 * Get current game state
 */
export async function getState(playerId?: string): Promise<GameState> {
  const url = playerId
    ? `${API_BASE}/api/state?player_id=${playerId}`
    : `${API_BASE}/api/state`;
  const response = await fetch(url, { headers });
  return response.json();
}

/**
 * Perform game action
 */
export async function doAction(
  playerId: string,
  action: 'join' | 'leave' | 'start' | 'submit',
  options?: {
    seat_index?: number;
    cards?: string[];
    start_level?: number;  // For testing: start at specific level (1-9)
  }
): Promise<ActionResponse> {
  const response = await fetch(`${API_BASE}/api/action`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      player_id: playerId,
      action,
      ...options,
    }),
  });
  return response.json();
}

/**
 * Join the game
 */
export function joinGame(playerId: string, seatIndex?: number) {
  return doAction(playerId, 'join', { seat_index: seatIndex });
}

/**
 * Leave current game
 */
export function leaveGame(playerId: string) {
  return doAction(playerId, 'leave');
}

/**
 * Start the game
 */
export function startGame(playerId: string, startLevel?: number) {
  return doAction(playerId, 'start', { start_level: startLevel });
}

/**
 * Submit answer (scan cards)
 */
export function submitAnswer(playerId: string, cards: string[]) {
  return doAction(playerId, 'submit', { cards });
}

